import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import institutionOptions from "../../data/institution_options.json";
import "./ProfessorPool.css";

// Department options for professors
const departmentOptions = [
  { value: "computer-science", label: "Computer Science" },
  { value: "electrical-engineering", label: "Electrical Engineering" },
  { value: "mechanical-engineering", label: "Mechanical Engineering" },
  { value: "civil-engineering", label: "Civil Engineering" },
  { value: "chemical-engineering", label: "Chemical Engineering" },
  { value: "biomedical-engineering", label: "Biomedical Engineering" },
  { value: "aerospace-engineering", label: "Aerospace Engineering" },
  { value: "industrial-engineering", label: "Industrial Engineering" },
  { value: "materials-science", label: "Materials Science" },
  { value: "nuclear-engineering", label: "Nuclear Engineering" },
  { value: "environmental-engineering", label: "Environmental Engineering" },
  { value: "physics", label: "Physics" },
  { value: "chemistry", label: "Chemistry" },
  { value: "biology", label: "Biology" },
  { value: "mathematics", label: "Mathematics" },
  { value: "statistics", label: "Statistics" },
  { value: "economics", label: "Economics" },
  { value: "psychology", label: "Psychology" },
  { value: "sociology", label: "Sociology" },
  { value: "political-science", label: "Political Science" },
  { value: "history", label: "History" },
  { value: "philosophy", label: "Philosophy" },
  { value: "english", label: "English" },
  { value: "literature", label: "Literature" },
  { value: "linguistics", label: "Linguistics" },
  { value: "art-history", label: "Art History" },
  { value: "music", label: "Music" },
  { value: "theater", label: "Theater" },
  { value: "film-studies", label: "Film Studies" },
  { value: "architecture", label: "Architecture" },
  { value: "urban-planning", label: "Urban Planning" },
  { value: "landscape-architecture", label: "Landscape Architecture" },
  { value: "other", label: "Other" },
];

function ProfessorPoolFilterBar({ onFilterChange }) {
  const [filters, setFilters] = useState({
    name: "",
    interests: "",
    institutions: [],
    departments: [],
  });

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Memoize the filter change handler to prevent unnecessary re-renders
  const handleFilterChange = useCallback(
    (newFilters) => {
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    },
    [onFilterChange]
  );

  // Update parent component when filters change
  useEffect(() => {
    handleFilterChange(filters);
  }, [filters, handleFilterChange]);

  // Individual filter handlers
  const handleTextInputChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleMultiSelectChange = (field, selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: selectedValues,
    }));
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "40px",
      fontSize: "14px",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const clearAllFilters = () => {
    setFilters({
      name: "",
      interests: "",
      institutions: [],
      departments: [],
    });
  };

  const toggleMoreFilters = () => {
    setShowMoreFilters((prev) => !prev);
  };

  return (
    <div className="professor-pool-filter-bar">
      <h2 className="filter-title">Filter Professors</h2>

      {/* Main Filters - Always Visible */}
      <div className="main-filters">
        {/* Professor Name Filter */}
        <div className="filter-group">
          <label className="filter-label">Professor Name</label>
          <input
            type="text"
            value={filters.name}
            onChange={(e) => handleTextInputChange("name", e.target.value)}
            className="filter-input"
            placeholder="e.g., John Smith"
          />
        </div>

        {/* Research Interests Filter */}
        <div className="filter-group">
          <label className="filter-label">Research Interests</label>
          <input
            type="text"
            value={filters.interests}
            onChange={(e) => handleTextInputChange("interests", e.target.value)}
            className="filter-input"
            placeholder="e.g., Machine Learning, NLP"
          />
        </div>

        {/* Institution Multi-Select */}
        <div className="filter-group">
          <label className="filter-label">Institution</label>
          <Select
            isMulti
            options={institutionOptions}
            value={institutionOptions.filter((opt) =>
              filters.institutions.includes(opt.value)
            )}
            onChange={(selectedOptions) =>
              handleMultiSelectChange("institutions", selectedOptions)
            }
            className="filter-select"
            classNamePrefix="select"
            placeholder="Select institutions..."
            styles={customSelectStyles}
            isClearable
            isSearchable
          />
        </div>

        {/* Department Multi-Select */}
        <div className="filter-group">
          <label className="filter-label">Department</label>
          <Select
            isMulti
            options={departmentOptions}
            value={departmentOptions.filter((opt) =>
              filters.departments.includes(opt.value)
            )}
            onChange={(selectedOptions) =>
              handleMultiSelectChange("departments", selectedOptions)
            }
            className="filter-select"
            classNamePrefix="select"
            placeholder="Select departments..."
            styles={customSelectStyles}
            isClearable
            isSearchable
          />
        </div>
      </div>

      {/* More Filters Toggle Button */}
      <div className="more-filters-toggle">
        <button
          onClick={toggleMoreFilters}
          className="toggle-more-button"
          aria-expanded={showMoreFilters}
        >
          <span>More Filters</span>
          <svg
            className={`toggle-icon ${showMoreFilters ? "rotated" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Collapsible More Filters Section */}
      <div
        className={`more-filters-section ${showMoreFilters ? "expanded" : "collapsed"
          }`}
      >
        <div className="more-filters-content">
          {/* More filters can be added here in the future */}
        </div>
      </div>

      {/* Clear All Filters Button */}
      <div className="filter-actions">
        <button onClick={clearAllFilters} className="clear-filters-button">
          Clear All Filters
        </button>
      </div>
    </div>
  );
}

export default ProfessorPoolFilterBar;
